/* eslint-disable */
/**
 * Trial API functions for Next.js
 *
 */

import { getProfile } from "@/profiles/getProfile"
import { env } from "@/lib/config/environments"

const profile = getProfile(process.env.NEXT_PUBLIC_PROFILE)
const baseUrl =
  env === "stage" || env === "qa"
    ? "https://cloud.stage.api.trimblecloud.com/business-systems/dx-trials/v1/trial"
    : env === "prod"
      ? "https://cloud.api.trimble.com/business-systems/dx-trials/v1/trial"
      : "https://cloud.api.trimble.com/business-systems/dx-trials/v1/trial"

export interface TrialLicense {
  sku: { id: string }
  status: "active" | "expired" | "pending"
  deactivationDate: string
}

export interface TrialApiResponse {
  trialId?: string
  trialStatus?: string
  deactivationDate?: string
  activationDate?: string
  isEligible?: boolean
}

export interface StartTrialResponse {
  trialId?: string
  entitlementId?: string
  skuId?: string
  accountId?: string
  licenseId?: string
  holderId?: string
  activationDate?: string
  deactivationDate?: string
  orderId?: string
  emsSource?: string
  status?: string
}

/**
 * Check if trial token is still valid (60 minute expiry)
 */
export const getTrialFormId = (timeStamp: number): boolean => {
  if (timeStamp) {
    const diff = (Date.now() - timeStamp) / 60000
    return diff <= 60
  }
  return false
}

/**
 * Start a new trial
 */
export const startTrial = (
  setLoading: (loading: boolean) => void,
  setError: (error: boolean) => void,
  setErrorText: (text: string) => void,
  sku?: string,
  accessToken?: string | null,
  onSuccess?: () => void,
): Promise<void> => {
  const token = accessToken ?? null

  if (!token) {
    setError(true)
    setErrorText("User not authenticated")
    return Promise.resolve()
  }

  if (!sku) {
    setError(true)
    setErrorText("Product SKU is required")
    return Promise.resolve()
  }

  setLoading(true)

  return fetch(`${baseUrl}/subscribe`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      trialId: sku,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Trial creation failed with status ${response.status}`)
      }

      return response.json()
    })
    .then(() => {
      onSuccess?.()

      // Reload page after successful trial start
      setTimeout(() => {
        if (typeof window !== "undefined") {
          window.location.reload()
        }
      }, 3000)
    })
    .catch(_error => {
      setError(true)
      setErrorText("Failed to start trial")
      setLoading(false)
    })
}

/**
 * Get trial details for a specific SKU.
 * When the user is eligible (no existing trial), optional `onEligible` runs
 * before loading ends — e.g. multi-account checks that may set an error.
 */
export const getTrialDetails = (
  sku: string,
  setActiveTrial: (trial: TrialApiResponse | null) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: boolean) => void,
  setTrialIdFromAPI: (trialId: string | undefined) => void,
  accessToken?: string | null,
  authTime?: string | null,
  onEligible?: () => void | Promise<void>,
): void => {
  const token = accessToken ?? null
  const authTimeVal = authTime ?? null

  if (!token) {
    setActiveTrial(null)
    setLoading(false)
    return
  }

  // Check if token has expired (60 minutes)
  if (authTimeVal && (Date.now() - Number(authTimeVal)) / 60000 > 60) {
    setActiveTrial(null)
    setLoading(false)
    return
  }

  setLoading(true)

  fetch(`${baseUrl}/eligibility/${sku}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then(response => response.json())
    .then(async (result: TrialApiResponse) => {
      if (result?.isEligible && result?.trialId) {
        setTrialIdFromAPI(result?.trialId ?? "")
        if (onEligible) {
          await onEligible()
        }
        setLoading(false)
      } else if (result?.isEligible === false) {
        setActiveTrial(result)
        setLoading(false)
        setTrialIdFromAPI(result?.trialId ?? "")
      } else {
        setLoading(false)
        setError(true)
      }
    })
    .catch(_error => {
      setActiveTrial(null)
      setLoading(false)
      setError(true)
    })
}

/**
 * Send data to Workato webhook
 */
export const sendToWorkato = (data: any): void => {
  const workatoProxy = "/.netlify/functions/workato-webhook"

  fetch(workatoProxy, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then(response => response.json())
    .then(_result => {
      // Successfully sent data to Workato
    })
    .catch(_error => {
      // Error sending data to Workato
    })
}

/**
 * Send lead data to Impartner via the Next.js route handler proxy.
 */
async function sendToImpartner(
  data: Record<string, unknown>,
  setError: (error: boolean) => void,
) {
  const impartnerProxy = "/api/impartner-lead"

  try {
    const response = await fetch(impartnerProxy, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      setError(true)
      const errorData = await response.json()
      console.error(
        "Error sending data to Impartner:",
        errorData || `Request failed with status code ${response.status}`,
      )
      throw new Error(`HTTP error! Status: ${response.status}`)
    }

    const responseData = await response.json()
    console.log("Response from Impartner:", responseData)
  } catch (error) {
    setError(true)
    console.error(
      "Network or API Error:",
      error instanceof Error ? error.message : error,
    )
    throw error
  }
}

export const submitToImpartner = async (
  partnerId: string,
  prefillData: { FirstName?: string; LastName?: string; Email?: string },
  formData: {
    JobTitle?: string
    PhoneNumber?: string
    Country?: string
    CompanyName?: string
    CompanySize?: string
    MarketSegment?: string
    IAgree?: boolean
  },
  setError: (error: boolean) => void,
  productName: string,
) => {
  const bodyData = {
    FirstName: prefillData?.FirstName,
    LastName: prefillData?.LastName,
    Title: formData?.JobTitle,
    Email: prefillData?.Email,
    partnerAccountId: partnerId,
    phone: formData?.PhoneNumber,
    mailingCountry: formData?.Country,
    customer: formData?.CompanyName,
    tcmaAdditionalInformation: formData?.CompanySize,
    tcmaTopic: formData?.MarketSegment,
    dateAssignedToPartner: new Date().toISOString(),
    tcmaGdprConfirmation: formData?.IAgree,
    source: productName,
  }

  await sendToImpartner(bodyData, setError)
}
